import { selectNav } from "@app/nav";
import { useSelector } from "@app/react";
import cn from "classnames";
import { useLayoutEffect, useState } from "react";
import { Outlet } from "react-router";
import { ApplicationSidebar, Footer, Group, TopPlanBanner } from "../shared";

const DEFAULT_NAV_COLLAPSE_WIDTH = 767;

interface Props {
  children?: React.ReactNode;
  header?: React.ReactNode;
  className?: string;
  padding?: boolean;
  mainWrapperDivClassName?: string;
  forceCollapseBelow?: number;
  allowResponsiveToggle?: boolean;
}

function useViewportWidth() {
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  useLayoutEffect(() => {
    function updateViewportWidth() {
      setViewportWidth(window.innerWidth);
    }

    window.addEventListener("resize", updateViewportWidth);
    updateViewportWidth();

    return () => window.removeEventListener("resize", updateViewportWidth);
  }, []);

  return viewportWidth;
}

export function AppSidebarLayout({
  children,
  header,
  className = "",
  padding = true,
  mainWrapperDivClassName = "",
  forceCollapseBelow = DEFAULT_NAV_COLLAPSE_WIDTH,
  allowResponsiveToggle = false,
}: Props) {
  const collapsed = useSelector(selectNav).collapsed;
  const viewportWidth = useViewportWidth();
  const isInForcedResponsiveRange = viewportWidth <= forceCollapseBelow;
  const canExpandInResponsiveRange =
    allowResponsiveToggle &&
    isInForcedResponsiveRange &&
    viewportWidth > DEFAULT_NAV_COLLAPSE_WIDTH;
  const [isResponsiveExpanded, setIsResponsiveExpanded] = useState(false);

  useLayoutEffect(() => {
    if (!canExpandInResponsiveRange) {
      setIsResponsiveExpanded(false);
    }
  }, [canExpandInResponsiveRange]);

  const effectiveCollapsed = isInForcedResponsiveRange
    ? !isResponsiveExpanded
    : collapsed;
  // Reduce collapsed width slightly to remove extra right gutter
  const sidebarWidth = effectiveCollapsed ? 56 : 256;
  const onToggleCollapsed = canExpandInResponsiveRange
    ? () => setIsResponsiveExpanded((expanded) => !expanded)
    : undefined;

  return (
    <div className={`flex h-full ${className}`}>
      <div
        className="flex flex-col fixed inset-y-0"
        style={{ width: sidebarWidth }}
      >
        <ApplicationSidebar
          collapsedOverride={
            isInForcedResponsiveRange ? effectiveCollapsed : undefined
          }
          disableToggle={
            isInForcedResponsiveRange && !canExpandInResponsiveRange
          }
          onToggleCollapsed={onToggleCollapsed}
        />
      </div>
      <div
        className={cn("h-full w-full flex flex-col", mainWrapperDivClassName)}
        style={{ marginLeft: sidebarWidth }}
      >
        <TopPlanBanner />
        <main className={`flex h-full flex-col ${padding ? "px-7 py-4" : ""}`}>
          <Group className="flex-1">
            {header ? header : null}
            {children ? children : <Outlet />}
          </Group>
          <Footer />
        </main>
      </div>
    </div>
  );
}
